import * as React from "react";

import { cn } from "@/lib/utils";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => {
    return (
      <textarea
        className={cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

const ResizableTextarea = ({ className, ...props }: TextareaProps) => {
  const [rows, setRows] = React.useState(1);
  const minRows = 1;
  const maxRows = 8;
  const ref = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (!ref.current) return;
    const textareaLineHeight = 20; // text-sm
    const previousRows = ref.current.rows;
    ref.current.rows = minRows; // reset number of rows in textarea
    const currentRows = ~~(ref.current.scrollHeight / textareaLineHeight);
    if (currentRows === previousRows) {
      ref.current.rows = currentRows;
    }
    if (currentRows >= maxRows) {
      ref.current.rows = maxRows;
      ref.current.scrollTop = ref.current.scrollHeight;
    }
    setRows(currentRows < maxRows ? currentRows : maxRows);
  }, [props.value]);

  return (
    <Textarea
      ref={ref}
      rows={rows}
      value={props.value}
      className={cn(
        "box-border h-auto min-h-0 w-full resize-none overflow-auto",
        className,
      )}
      {...props}
    />
  );
};

export { Textarea, ResizableTextarea };
